import {Icon} from '@iconify/react';

import React, {useCallback, useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import ProductDetailPanel from './productDetailPanel';

import BolmateTooltip from '../../controls/tooltip/bolmateTooltip';

import {SearchRankingProduct, SearchRankingsColumnDef, TrackedKeyword} from '../../../util/interfaces';
import {SearchRankingMode} from '../../../util/types';

import {showSnackbar} from '@dvrd/dvr-controls';

interface Props {
    product: SearchRankingProduct;
    columns: Array<SearchRankingsColumnDef>;
    isExpanded: boolean;
    onToggleExpand: () => void;
    accountId: string;
    accountIds: Array<string>;
    countryCodes: Array<'NL' | 'BE'>;
    hoveredColIndex: number;
    onColHover: (index: number) => void;
    onAddKeyword: (
        keyword: string,
        ean: string,
        callback: (keyword: TrackedKeyword, restored: boolean) => void
    ) => void;
    onKeywordsChanged: VoidFunction;
    rankingMode: SearchRankingMode;
    manualKeywordCount: number;
    manualKeywordLimit: number;
    hasModule: boolean;
}

export default function ProductRow(props: Props) {
    const {t} = useTranslation();
    const {
        product,
        columns,
        isExpanded,
        onToggleExpand,
        accountId,
        accountIds,
        countryCodes,
        onAddKeyword,
        hoveredColIndex,
        onColHover,
        rankingMode,
        onKeywordsChanged,
        manualKeywordCount,
        manualKeywordLimit,
        hasModule
    } = props;
    const [imgError, setImgError] = useState(false);
    const copyEan = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            navigator.clipboard
                .writeText(product.ean)
                .then(() => showSnackbar(t('searchRankings.table.eanCopied', 'EAN gekopieerd')));
        },
        [product.ean]
    );
    const detailRowRef = useRef<HTMLTableRowElement>(null);

    useEffect(() => {
        if (isExpanded) detailRowRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, [isExpanded]);

    return (
        <>
            <tr
                className={`product-row${isExpanded ? ' expanded' : ''}`}
                onClick={onToggleExpand}
                onMouseLeave={() => onColHover(-1)}
            >
                {/* Product always visible, hardcoded */}
                <td onMouseEnter={() => onColHover(0)}>
                    <div className='product-cell'>
                        <div className='product-image-wrapper'>
                            {product.image && !imgError ? (
                                <img src={product.image} alt={product.name} onError={() => setImgError(true)} />
                            ) : (
                                <div className='product-image-placeholder'>
                                    <Icon
                                        icon='mdi:package-variant-closed'
                                        style={{fontSize: '1.25rem', color: '#D1D5DB'}}
                                    />
                                </div>
                            )}
                            <div className={`expand-indicator${isExpanded ? ' expanded' : ''}`}>
                                <Icon icon='mdi:chevron-down' style={{fontSize: '0.8125rem', color: '#9CA3AF'}} />
                            </div>
                        </div>
                        <div className='product-info'>
                            <div className='product-name'>{product.name}</div>
                            <div className='product-ean' onClick={copyEan}>
                                EAN: {product.ean}
                                <Icon
                                    icon='mdi:content-copy'
                                    style={{fontSize: 'inherit', marginLeft: '0.25rem', opacity: 0.5}}
                                />
                            </div>
                            <BolmateTooltip text={t('searchRankings.table.viewOnBol', 'Bekijk op bol.com')}>
                                <a
                                    className='product-bol-link'
                                    href={`https://www.bol.com/nl/nl/s/?searchtext=${product.ean}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    onClick={e => e.stopPropagation()}
                                >
                                    <Icon icon='mdi:open-in-new' style={{fontSize: '0.75rem'}} />
                                    <span>bol</span>
                                </a>
                            </BolmateTooltip>
                        </div>
                    </div>
                </td>
                {/* Dynamic columns */}
                {!product.keyword_count ? (
                    <td colSpan={columns.length} style={{width: '70%'}}>
                        <div className='no-keywords-container'>
                            <div className='label-icon-box'>
                                <Icon icon='mdi:bar-chart' className='label-icon' />
                            </div>
                            <span className='no-keywords-label'>
                                {t('searchRankings.table.noDataAddKeyword', 'Geen data van bol. Voeg keywords toe.')}
                            </span>
                            <div className='label-icon-box'>
                                <Icon icon='mdi:plus' className='label-icon' />
                            </div>
                        </div>
                    </td>
                ) : (
                    columns.map((col, idx) => {
                        return (
                            <td
                                key={col.field}
                                className={`${col.className}${hoveredColIndex === idx + 1 ? ' col-hovered' : ''}`}
                                style={col.centered ? {textAlign: 'center'} : undefined}
                                onMouseEnter={() => onColHover(idx + 1)}
                            >
                                {col.body(product)}
                            </td>
                        );
                    })
                )}
            </tr>

            {isExpanded && (
                <tr className='detail-row' ref={detailRowRef}>
                    <td colSpan={1 + columns.length}>
                        <ProductDetailPanel
                            accountId={accountId}
                            accountIDs={accountIds}
                            ean={product.ean}
                            countryCodes={countryCodes}
                            onClose={onToggleExpand}
                            onAddKeyword={onAddKeyword}
                            rankingMode={rankingMode}
                            onKeywordsChanged={onKeywordsChanged}
                            manualKeywordCount={manualKeywordCount}
                            manualKeywordLimit={manualKeywordLimit}
                            hasModule={hasModule}
                        />
                    </td>
                </tr>
            )}
        </>
    );
}
